import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'

// Opens the existing legal page in a new tab. stopPropagation keeps the click
// from toggling the surrounding consent checkbox; no preventDefault so the link
// still navigates.
function LegalLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{
        color: 'var(--accent)', textDecoration: 'underline',
        cursor: 'pointer', fontWeight: 600,
      }}
    >
      {children}
    </a>
  )
}

function ConsentCheckbox({ checked, onChange, children }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: '.6rem',
      cursor: 'pointer', fontSize: '.82rem', color: 'var(--text)', lineHeight: 1.55,
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          width: 18, height: 18, marginTop: '.1rem',
          accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0,
        }}
      />
      <span>{children}</span>
    </label>
  )
}

/**
 * Blocking modal shown when a logged-in user has not yet accepted the required
 * confirmations (legacy users who registered before the consent gate existed).
 * New users always have consentAccepted === true, so they never see this.
 */
export default function ConsentGate() {
  const { user, refreshAuth, logout } = useAuth()
  const navigate = useNavigate()

  const [ageOk, setAgeOk] = useState(false)
  const [termsOk, setTermsOk] = useState(false)
  const [profileOk, setProfileOk] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Nothing to show: not logged in, or consent already accepted.
  if (!user || user.consentAccepted === true) return null

  const consentOk = ageOk && termsOk && profileOk

  const confirm = async () => {
    if (!consentOk || loading) return
    setError('')
    setLoading(true)
    try {
      await api.post('/profile/accept-consent')
      // Refresh auth so consentAccepted=true and the gate self-hides.
      await refreshAuth()
    } catch {
      setError('Təsdiq zamanı xəta baş verdi. Yenidən cəhd edin.')
    } finally {
      setLoading(false)
    }
  }

  const decline = () => {
    logout()
    navigate('/login')
  }

  return (
    <>
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 3000,
          background: 'rgba(0,0,0,.82)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <div
          className="card"
          style={{
            width: '100%', maxWidth: 540, maxHeight: 'min(90vh, 760px)',
            display: 'flex', flexDirection: 'column',
            padding: 0, overflow: 'hidden',
          }}
        >
          <div style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid var(--border)', flexShrink: 0,
          }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, color: 'var(--text)' }}>
              Davam etmək üçün təsdiq edin
            </h2>
            <p style={{ fontSize: '.82rem', color: 'var(--muted)', margin: '.4rem 0 0', lineHeight: 1.5 }}>
              Platformadan istifadəni davam etdirmək üçün aşağıdakıları təsdiq etməlisiniz.
            </p>
          </div>

          <div style={{
            overflowY: 'auto', padding: '1.5rem',
            display: 'flex', flexDirection: 'column', gap: '.85rem',
          }}>
            <ConsentCheckbox checked={ageOk} onChange={setAgeOk}>
              18 yaşımı tamamladığımı təsdiq edirəm
            </ConsentCheckbox>

            <ConsentCheckbox checked={termsOk} onChange={setTermsOk}>
              <LegalLink href="/terms">İstifadə Şərtləri</LegalLink>
              {' və '}
              <LegalLink href="/privacy">Məxfilik Siyasəti</LegalLink>
              {' ilə tanış oldum və qəbul edirəm'}
            </ConsentCheckbox>

            <ConsentCheckbox checked={profileOk} onChange={setProfileOk}>
              Yaratdığım iş profilindəki məlumatların digər istifadəçilərə görünəcəyini başa düşürəm
            </ConsentCheckbox>

            {error && <p className="form-error" style={{ margin: 0 }}>{error}</p>}
          </div>

          <div style={{
            padding: '1.1rem 1.5rem',
            borderTop: '1px solid var(--border)', flexShrink: 0,
            display: 'flex', flexDirection: 'column', gap: '.6rem',
          }}>
            <button
              type="button"
              className="btn btn-primary btn-full"
              disabled={!consentOk || loading}
              onClick={confirm}
            >
              {loading ? <span className="spinner" /> : 'Təsdiqlə və davam et'}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-full"
              disabled={loading}
              onClick={decline}
            >
              İmtina et
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
